<script setup>
import { computed } from 'vue';
import { formatDate } from 'date-fns';
import { storeToRefs } from 'pinia';

import { SHORT_URL_API } from '@/config/appConfig';

import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Skeleton from 'primevue/skeleton';

import useTrackingLinksStore from '@/stores/tracking-links';

const toast = useToast();

const {
  isFetching,
  trackingLink,
  userMap,
} = storeToRefs(useTrackingLinksStore());

const page = computed(() => trackingLink?.value?.currentDestination?.landingPage);
const trackingLinkUrl = computed(() => (
  trackingLink.value ? `${SHORT_URL_API}/t/${trackingLink.value.rid}` : ''
));

const pageTitle = computed(() => page.value?.title);
const pagePublishedAt = computed(() => {
  const date = page.value?.createdAt;
  return date ? formatDate(new Date(date), 'MM/dd/yyyy hh:mma') : 'N/A';
});

const destinationUrl = computed(() => trackingLink.value?.currentDestination?.destinationUrl);

const createdBy = computed(() => {
  return userMap.value[trackingLink.value.createdBy] || {};
});

const copyLink = async (link) => {
  try {
    await navigator.clipboard.writeText(link);

    toast.add({
      severity: 'success',
      summary: 'Link has been copied!',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Failed to copy link',
      detail: error.message,
      life: 5000,
    });
  }
};
</script>

<template>
  <!-- Link Metadata -->
  <Card
    pt:root:class="analytics-card analytics-card__metadata shadow-2 mb-3"
    pt:body:class="w-full"
  >
    <template #content>
      <div v-if="isFetching" class="flex justify-content-center align-items-center">
        <Skeleton height="4rem" width="4rem" />
        <Skeleton height="4rem" width="4rem" />
        <Skeleton height="4rem" width="4rem" />
        <Skeleton height="4rem" width="4rem" />
      </div>

      <Transition name="fade-in-out">
        <div
          v-if="!isFetching"
          class="flex justify-content-between gap-5"
        >
          <!-- Page Metadata -->
          <div
            v-if="page && pageTitle"
            class="analytics-card__metadata-item flex-shrink-0"
            :title="pageTitle"
          >
            <h4 class="text-xs m-0 text-400 mb-1">
              Page
            </h4>

            <div class="flex">
              <div class="analytics-card__metadata-item-value">
                {{ pageTitle }}
              </div>
            </div>
          </div>

          <!-- End Page Metadata -->
          <div
            v-else
            class="analytics-card__metadata-item"
            :title="destinationUrl"
          >
            <h4 class="text-xs m-0 text-400 mb-1">
              Destination URL
            </h4>

            <div class="flex">
              <div class="analytics-card__metadata-item-value">
                {{ destinationUrl }}
              </div>

              <div class="ml-2">
                <FontAwesomeIcon
                  class="cursor-pointer"
                  role="button"
                  aria-label="Copy destination URL"
                  v-tooltip="'Copy destination URL'"
                  title="Copy destination URL"
                  @click="copyLink(destinationUrl)"
                  icon="far fa-copy"
                  data-entity="app-trackingLinks-copyDestinationUrl"
                />
              </div>
            </div>
          </div>

          <div
            class="analytics-card__metadata-item flex-shrink-0"
            :title="trackingLinkUrl"
          >
            <h4 class="text-xs m-0 text-400 mb-1">
              {{ page ? 'URL' : 'Tracking URL' }}
            </h4>

            <div class="flex">
              <div class="analytics-card__metadata-item-value">
                {{ trackingLinkUrl }}
              </div>

              <div class="ml-2">
                <font-awesome-icon
                  class="cursor-pointer"
                  role="button"
                  aria-label="Copy tracking URL"
                  v-tooltip="'Copy tracking URL'"
                  title="Copy tracking URL"
                  @click="copyLink(trackingLinkUrl)"
                  icon="far fa-copy"
                  data-entity="app-trackingLinks-copyTrackingUrl"
                />
              </div>
            </div>
          </div>

          <!-- Created By -->
          <div
            v-if="createdBy"
            class="analytics-card__metadata-item flex-shrink-0"
            :title="destinationUrl"
          >
            <h4 class="text-xs m-0 text-400 mb-1">
              Created By
            </h4>

            <div class="flex">
              <div class="analytics-card__metadata-item-value flex align-items-center">
                {{ createdBy.displayName }}
              </div>
            </div>
          </div>
          <!-- End Created By -->

          <!-- Page Metadata -->
          <div
            v-if="page"
            class="analytics-card__metadata-item flex-shrink-0"
          >
            <h4 class="text-xs m-0 text-400 mb-1">
              Published At
            </h4>

            <div class="flex">
              <div class="analytics-card__metadata-item-value flex flex-column">
                <div>{{ pagePublishedAt }}</div>
              </div>
            </div>
          </div>
          <!-- End Page Metadata -->
        </div>
      </Transition>
    </template>
  </Card>
  <!-- End Link Metadata -->
</template>
